import { z } from 'zod'

import { jsonish } from './json'

export const SELLER_AUTH_CODE_LENGTH = 6

export const sellerIdSchema = z
  .string({
    required_error: 'errors.missing_id',
    invalid_type_error: 'errors.invalid_id',
  })
  .length(14, { message: 'errors.invalid_id' })
  .startsWith('slr_', { message: 'errors.invalid_id' })
  .describe('Seller id. 14 characters, prefixed `slr_`.')

const emailSchema = z
  .string()
  .email({ message: 'errors.email_is_invalid' })
  .min(5, { message: 'errors.email_is_too_short' })
  .max(255, { message: 'errors.email_is_too_long' })
  .describe('Email address of the seller.')

const authCodeSchema = z
  .string()
  .min(SELLER_AUTH_CODE_LENGTH, {
    message: 'errors.authentication_code_is_too_short',
  })
  .max(SELLER_AUTH_CODE_LENGTH, {
    message: 'errors.authentication_code_is_too_long',
  })

export const HONEYPOT_FIELD_NAME = 'subdomain'

export const sellerRegisterSchema = z.object({
  email: emailSchema,
})

export const sellerLoginSchema = z.object({
  email: emailSchema,
  [HONEYPOT_FIELD_NAME]: z.string().optional(),
})

export const sellerAuthenticateSchema = z.object({
  code: authCodeSchema,
})

const notificationCategorySchema = z
  .enum(['orders.new', 'news'])
  .describe('`orders.new` fires on a paid order. `news` is product news.')
const notificationMethodSchema = z.enum(['email', 'push'])
const notificationMethodsSchema = z
  .array(notificationMethodSchema)
  .describe('Methods on for this category. An empty array mutes it.')

export const sellerNotificationSettingsSchema = jsonish(
  z
    .record(notificationCategorySchema, notificationMethodsSchema)
    .or(z.literal('').transform(() => null))
    .or(z.null())
    .describe(
      'Which notification methods are on for each category. `null` clears every setting.'
    )
)

export type SellerNotificationSettings = z.infer<
  typeof sellerNotificationSettingsSchema
>

export const patchSellerSchema = z
  .object({
    name: z.string().optional().describe('Display name of the seller.'),
    notificationSettings: sellerNotificationSettingsSchema,
  })
  .partial()

export type PatchSeller = z.infer<typeof patchSellerSchema>
